import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AccountIssueTypes, Services as AccountServices } from '@smiles/core-account';
import SuspendedUserModal from 'components/SuspendedUserModal';
import UploadUserFilesModal from 'components/UploadUserFilesModal';
import SkipUploadFilesModal from 'components/SkipUploadFilesModal';
import ErrorModal from 'components/ErrorModal';
import { useHistory } from 'react-router-dom';
import decodeJWT from 'jsonwebtoken';
import SuspendedAccountWrapper from 'components/SuspendedAccountWrapper';
import { loginDataLayer } from 'utils/dataLayer';
import { Config } from '../../services/core/config';

export type SuspendedUserProps = {
  checkingSuspended?: boolean;
};

type UserData = {
  identifier: string;
  name: string;
  email: string;
  cellPhone: {
    DDI: string;
    DDD: string;
    number: string;
  };
  attachment?: [
    {
      url: string;
      comments: string;
    },
  ];
};

const SuspendedUser = ({ checkingSuspended = false }: SuspendedUserProps) => {
  const TOKEN_NAME = process.env.REACT_APP_TOKEN_NAME || 'session-token';
  const [showUploadPage, setShowUploadPage] = useState(false);
  const [openModalUserData, setOpenModalUserData] = useState(false);
  const [userData, setUserData] = useState<UserData>();
  const [isLoading, setIsLoading] = useState(false);
  const [skipUploadFiles, setSkipUploadFiles] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const history = useHistory();
  const decodedToken = useMemo(() => {
    return decodeJWT.decode(localStorage.getItem(TOKEN_NAME) || '');
  }, []) as any;
  const memberStatus = useMemo(() => decodedToken['https://smiles.api/memberStatus'], [decodedToken]);
  const inactiveUserSubPage = useMemo(() => loginDataLayer.inactiveUserSubPage(memberStatus), [memberStatus]);
  const allowedMemberStatus = useMemo(() => ['SUSPENDED', 'PVSM SUSPENDED', 'DECEASED', 'CANCELLED'], []);

  useEffect(() => {
    loginDataLayer.triggerPageViewEvent(inactiveUserSubPage, 2);
  }, []);

  useEffect(() => {
    if (openModalUserData === true) {
      loginDataLayer.triggerPageViewEvent(`${inactiveUserSubPage}/dados-pessoais`, 2);
    }
  }, [openModalUserData]);

  useEffect(() => {
    if (skipUploadFiles === true) {
      loginDataLayer.triggerPageViewEvent(`${inactiveUserSubPage}/sem-documentos`, 2);
    }
  }, [skipUploadFiles]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_NAME) || '';
    Config.saveTokenAuthUser({ accessToken: token });
    try {
      const logoLink = document.querySelector('a:has(.smls-logos.smiles)');
      if (logoLink) {
        logoLink.addEventListener('click', () => localStorage.removeItem(TOKEN_NAME));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (memberStatus && !allowedMemberStatus.includes(memberStatus)) {
      history.push('/');
    }
  }, [history, allowedMemberStatus, memberStatus]);

  const handleUserDataForm =
    async (formData: UserData) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    localStorage.removeItem('session-token');
    if (memberStatus === 'CANCELLED') {
      setUserData(formData);
      setIsLoading(false);
      setShowUploadPage(true);
      setOpenModalUserData(false);
      return;
    }
    const { identifier, ...formFields } = formData;
    const parsedIdentifier = identifier.replace(/[.-]+/g, '');
    const accountPayload: AccountIssueTypes.AccountIssueServiceRequestType = {
      ...formFields,
    };
    if (parsedIdentifier.length > 9) {
      accountPayload.document = {
        type: 'CPF',
        value: parsedIdentifier,
      };
    } else {
      accountPayload.memberNumber = identifier;
    }

    try {
      const { success } = await AccountServices.accountIssue(accountPayload);

      if (!success) {
        throw new Error();
      }
      history.push('/validar/sucesso');
    } catch (error) {
      setShowErrorModal(true);
      setOpenModalUserData(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserFilesForm =
    async (files: any) => {
    if (isLoading) {
      return;
    }

    if (userData) {
      setIsLoading(true);
      const { identifier, ...formFields } = userData;
      const parsedIdentifier = identifier.replace(/[.-]+/g, '');
      const accountPayload: AccountIssueTypes.AccountIssueServiceRequestType = {
        ...formFields,
        attachment: files,
      };
      if (parsedIdentifier.length > 9) {
        accountPayload.document = {
          type: 'CPF',
          value: parsedIdentifier,
        };
      } else {
        accountPayload.memberNumber = identifier;
      }
      try {
        const { success } = await AccountServices.accountIssue(accountPayload);

        if (!success) {
          throw new Error();
        }
        history.push('/validar/sucesso', { memberStatus });
      } catch (error) {
        setSkipUploadFiles(false);
        setOpenModalUserData(false);
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBackButton = useCallback(() => {
    setShowUploadPage(false);
    setOpenModalUserData(true);
  }, []);

  const onToggleModal = useCallback(() => {
    localStorage.removeItem(TOKEN_NAME);
  }, []);

  const onCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const handleSkipButton = useCallback(() => {
    setSkipUploadFiles((oldValue) => !oldValue);
  }, []);

  const handleSkipConfirmation =
    (confirm: boolean) => {
    if (!confirm) {
      setSkipUploadFiles(false);
      return;
    }
    handleUserFilesForm([]);
  };

  const handleFillForm = useCallback(() => {
    setOpenModalUserData(true);
  }, []);

  const handleShowPhones = useCallback(() => {
    window.location.href = '/cadastro/central';
  }, []);

  return (
    <SuspendedAccountWrapper
      onPrimaryClik={checkingSuspended ? onToggleModal : handleFillForm}
      showSecondaryButton={memberStatus === 'CANCELLED'}
      onSecondaryClick={handleShowPhones}
      showSecondaryText={checkingSuspended}
    >
      <div className="smls-suspended-user-main-content">
        <SuspendedUserModal
          isOpen={openModalUserData}
          isLoading={isLoading}
          toggleModal={onToggleModal}
          onSubmit={handleUserDataForm}
        />
        <UploadUserFilesModal
          isOpen={showUploadPage && !skipUploadFiles && !showErrorModal}
          isLoading={isLoading}
          onSubmit={handleUserFilesForm}
          onBackButton={handleBackButton}
          onSkipButton={handleSkipButton}
        />
        <SkipUploadFilesModal
          isOpen={skipUploadFiles}
          isLoading={isLoading}
          onConfirmation={handleSkipConfirmation}
          toggleModal={() => handleSkipConfirmation(false)}
        />
        <ErrorModal isOpen={showErrorModal} onClose={onCloseErrorModal} toggleModal={onCloseErrorModal} />
      </div>
    </SuspendedAccountWrapper>
  );
};

export default SuspendedUser;
